import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SettingsService } from '@app/_services/settings.service';
import { TranslationKey } from '@app/_generated/translation-keys';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { CarouselComponent, CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { FeatureGuideComponent } from '../feature-guide/feature-guide.component';
import { DependencyService } from '@app/_services/dependency.service';
import { DomSanitizer } from '@angular/platform-browser';
import { StorageService } from '@app/_services/storage.servive';
import { ModalService } from '@app/_services/modal.service';
import { CacheService } from '@app/_services/cache.service';
import { AlertService } from '@app/_services/alert.service';
import { BRAND_FULL_LOGO } from '@app/_utils/brand-icons';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
  selector: 'app-feature-intro',
  imports: [
    CommonModule,
    SvgComponent,
    CarouselModule
  ],
  templateUrl: './feature-intro.component.html',
  styleUrl: './feature-intro.component.scss'
})
export class FeatureIntroComponent {

  readonly brandFullLogo = BRAND_FULL_LOGO;
  readonly BRAND_CONFIG = BRAND_CONFIG;
  @ViewChild('introCarousel', { static: true }) introCarousel!: CarouselComponent;
  introOptions: OwlOptions = {};
  currentPosition: number = 0;
  featureIntros: { id: string, image: string, content: TranslationKey, secondaryButton?: TranslationKey, title?: TranslationKey }[] = [
    { id: 'welcome', image: 'assets/imgs/intro/intro1.png', content: 'featureIntro_welcomeContent', title: 'welcomeToBrand_title' },
    { id: 'allInOne', image: 'assets/imgs/intro/intro2.png', content: 'featureIntro_allInOneContent', secondaryButton: 'featureIntro_exploreFeatures', title: 'featureIntro_allInOneTitle' },
    { id: 'collaboration', image: 'assets/imgs/intro/intro3.png', content: 'featureIntro_collaborateContent', secondaryButton: 'featureGuide_title', title: 'common_collaborate' },
    { id: 'publicShare', image: 'assets/imgs/intro/intro4.png', content: 'featureIntro_publicShareContent', title: 'featureIntro_publicShareTitle' }
  ];

  constructor(
    public ss: SettingsService,
    private modalService: ModalService,
    public cc: CacheService,
    private sanitizer: DomSanitizer,
    private ds: DependencyService,
    private storageService: StorageService,
    private router: Router,
    private alertService: AlertService
  ) {

    this.introOptions = {
      loop: false,
      margin: 0,
      nav: false,
      dots: false,
      items: 1,
      startPosition: 0,
      rtl: this.ss.appDirection() === 'rtl',
    };

    this.currentPosition = 0;
  }

  onItemChanged(event: any) {
    this.currentPosition = event.startPosition;
  }

  nextSlide() {
    if (this.currentPosition === 3) {
      this.storageService.setIsViewedFeatureIntro(true);
      this.router.navigate(['/today']);
      this.checkForAppleProxyEmail();
      return;
    } else {
      this.introCarousel.next();
    }
  }

  /** Check if user is using Apple's "Hide My Email" relay and show warning dialog */
  private checkForAppleProxyEmail() {
    const email = this.cc.user?.email;
    if (!email) return;

    const appleRelayDomain = 'privaterelay.appleid.com';
    if (email.toLowerCase().includes(appleRelayDomain)) {
      this.showAppleProxyEmailDialog(email);
    }
  }

  private showAppleProxyEmailDialog(email: string) {
    this.alertService.dynamicDialog({
      headerIcon: { type: 'svg', name: 'importantAlert', color: this.ss.theme().color14.toString() },
      title: this.ss.t('appleProxyAlert_title'),
      contentBlocks: [
        { text: this.ss.t('appleProxyAlert_content1'), textClass: 'color-7' },
        { text: email, textClass: 'color-8 text-center' },
        { text: this.ss.t('appleProxyAlert_content2'), textClass: 'color-7' },
        { text: this.ss.t('appleProxyAlert_content3'), textClass: 'color-7' },
      ],
      buttons: [
        { text: this.ss.t('common_ok'), color: 'color-35', action: 'confirm' }
      ]
    });
  }

  getContent(id: TranslationKey) {
    const rawHtml = this.ss.t(id);
    const modifiedHtml = this.ds.computeStyles(rawHtml);
    return this.sanitizer.bypassSecurityTrustHtml(modifiedHtml);
  };

  openFeatureGuide() {
    // Check screen size to determine modal level
    const isNarrow = this.cc.isNarrowScreen() || window.innerWidth <= 750;

    if (isNarrow) {
      this.modalService.openMainContent(FeatureGuideComponent, {});
    } else {
      this.modalService.openLevel1(FeatureGuideComponent, {});
    }
  };
}
